/**
 * Client-side authorization functions and types
 * This file contains client-specific authorization logic and imports synced types
 */

import { useGetUserAbilitiesQuery } from '../../../types/generated-types';
import { useAuthenticator } from '../AuthenticationContext';
import { createMongoAbility } from '@casl/ability';

// Import synced types from the auto-generated file
import type { Actions, Subjects } from './synced-abilities';

export type {
  Actions,
  SubjectTypes,
  BaseSubject,
  Subjects,
  AppAbility,
} from './synced-abilities';

export interface AbilityRule {
  action: string;
  subject: string;
  conditions?: any;
  inverted: boolean;
  reason?: string;
}

export interface UserAbilities {
  rules: AbilityRule[];
}

/**
 * Hook to get user abilities from the server
 */
export function useServerAbilities() {
  const { user } = useAuthenticator();

  const { data, loading, error } = useGetUserAbilitiesQuery({
    skip: !user,
    fetchPolicy: 'cache-and-network',
  });

  return {
    abilities: data?.userAbilities as UserAbilities | undefined,
    loading,
    error,
  };
}

/**
 * Hook that provides a CASL ability for the current user
 */
export function useServerAbility() {
  const { abilities, loading, error } = useServerAbilities();

  if (loading || error) {
    return {
      ability: createMongoAbility<[Actions, Subjects]>([] as any),
      loading,
      error,
    };
  }

  // Parse the rules to handle JSON string arrays for action and subject
  const parsedRules =
    abilities?.rules?.map((rule) => {
      let action = rule.action;
      let subject = rule.subject;

      // Try to parse action if it looks like a JSON array
      if (
        typeof action === 'string' &&
        action.startsWith('[') &&
        action.endsWith(']')
      ) {
        try {
          action = JSON.parse(action);
        } catch (e) {
          // If parsing fails, keep the original string
          console.warn('Failed to parse action as JSON:', action);
        }
      }

      // Try to parse subject if it looks like a JSON array
      if (
        typeof subject === 'string' &&
        subject.startsWith('[') &&
        subject.endsWith(']')
      ) {
        try {
          subject = JSON.parse(subject);
        } catch (e) {
          // If parsing fails, keep the original string
          console.warn('Failed to parse subject as JSON:', subject);
        }
      }

      return {
        ...rule,
        action,
        subject,
      };
    }) ?? [];

  // Create a CASL ability from the server abilities
  const ability = createMongoAbility<[Actions, Subjects]>(parsedRules as any);

  return {
    ability,
    loading,
    error,
  };
}

/**
 * Adapts GraphQL types to CASL-compatible format
 * This function converts GraphQL object types to the format expected by CASL
 * Handles immutable/frozen objects by creating new objects instead of modifying existing ones
 */
export function adaptForCasl<T extends { __typename?: string; _id?: string }>(
  obj: T,
): T & { _id: string } {
  if (!obj) {
    return obj as any;
  }

  // Create a new object to avoid modifying immutable objects
  const newObj = { ...obj } as T & { _id: string };

  // If the object already has an _id, return the new object as is
  if ('_id' in obj && obj._id) {
    return newObj;
  }

  // If the object has an id field, use it as _id
  if ('id' in obj && obj.id) {
    newObj._id = obj.id as string;
    return newObj;
  }

  // If no id found, return the new object as is (CASL will handle the error)
  return newObj;
}
